// Presentational Toast molecule. Wraps `@radix-ui/react-toast` primitives so
// Radix owns the WAI-ARIA + behavior surface (live-region politeness, Esc
// dismissal, swipe-to-dismiss, focus-via-viewport hotkey, portal mounting) and
// this component owns the visual + variant surface.
//
// Politeness mapping: variant='success' → type='background' (aria-live=polite),
// variant='error' → type='foreground' (aria-live=assertive). Foreground also
// pauses Radix's auto-dismiss timer, which is the right default for an error
// the Manager needs to react to.
//
// Lives inside the NotificationProvider (notification-provider.tsx). Direct
// rendering also works when a consumer wraps it in its own RadixToast.Provider
// + Viewport (used by the unit tests).

import * as RadixToast from '@radix-ui/react-toast';
import { AlertCircle, Check } from 'lucide-react';
import { type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './toast.css';

type ToastVariant = 'success' | 'error';

interface ToastProps {
	id?: string;
	variant: ToastVariant;
	open: boolean;
	on_open_change: (open: boolean) => void;
	duration_ms?: number;
	title: ReactNode;
	action?: ReactNode;
	className?: string;
}

const Toast = ({
	id,
	variant,
	open,
	on_open_change,
	duration_ms,
	title,
	action,
	className,
}: ToastProps) => {
	// Instance-unique default id: several toasts routinely stack in the
	// viewport at once. An explicit `id` prop still wins.
	const auto_id = useId();
	return (
		<RadixToast.Root
			id={id ?? `Toast-${auto_id}`}
			open={open}
			onOpenChange={on_open_change}
			duration={duration_ms}
			type={variant === 'error' ? 'foreground' : 'background'}
			className={classlist('toast', `toast--${variant}`, className)}
		>
			<div className='toast__icon' aria-hidden='true'>
				{variant === 'success' ? (
					<Check className='icon icon--sm' />
				) : (
					<AlertCircle className='icon icon--sm' />
				)}
			</div>
			<div className='toast__body'>
				<RadixToast.Title className='toast__title'>{title}</RadixToast.Title>
			</div>
			{action ? <div className='toast__action'>{action}</div> : null}
		</RadixToast.Root>
	);
};

export type { ToastProps, ToastVariant };
export { Toast };
